import { useEffect, useId, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Spinner } from "./Spinner";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyText?: string;
  className?: string;
  id?: string;
  /** Applied to the trigger `<button>` only (not the search input or listbox). */
  "data-testid"?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  isLoading = false,
  emptyText = "No results",
  className,
  id,
  "data-testid": dataTestId,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // -1 = no active item; first ArrowDown moves to 0 (W3C ARIA combobox convention)
  const [activeIndex, setActiveIndex] = useState(-1);
  const generatedId = useId();
  const listboxId = `ss-lb-${generatedId}`;

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );
  const selectedLabel = options.find((o) => o.value === value)?.label ?? null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset index whenever filtered list changes (derived from search) — intentional one-way dependency
  useEffect(() => {
    setActiveIndex(-1);
  }, [search]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent) {
    const count = filtered.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (count > 0) setActiveIndex((i) => (i < 0 ? 0 : (i + 1) % count));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (count > 0) setActiveIndex((i) => (i < 0 ? count - 1 : (i - 1 + count) % count));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) {
        onChange(opt.value);
        setOpen(false);
      }
    }
  }

  function select(val: string) {
    onChange(val);
    setOpen(false);
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
    >
      <Popover.Trigger asChild>
        <button
          id={id}
          data-testid={dataTestId}
          type="button"
          disabled={disabled || isLoading}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          className={[
            "w-full flex items-center justify-between gap-2 rounded-sm border border-border",
            "bg-surface px-3 py-2 text-body text-left cursor-pointer",
            "focus:outline-none focus:ring-2 focus:ring-accent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <span className={selectedLabel ? "text-text" : "text-text-muted"}>
            {isLoading ? "Loading…" : (selectedLabel ?? placeholder)}
          </span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-text-muted flex-shrink-0"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          sideOffset={4}
          style={{ width: "var(--radix-popover-trigger-width)" }}
          className="z-50 rounded-sm border border-border bg-surface-raised shadow-lg overflow-hidden outline-none"
          onKeyDown={handleKeyDown}
        >
          <input
            autoFocus
            role="searchbox"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={
              activeIndex >= 0 && filtered[activeIndex] ? `${listboxId}-${activeIndex}` : undefined
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full px-3 py-2 text-body bg-transparent border-b border-border outline-none placeholder:text-text-muted"
          />
          <ul id={listboxId} role="listbox" className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <li className="flex justify-center py-4" aria-hidden="true">
                <Spinner className="h-4 w-4" />
              </li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-body text-text-muted">
                {emptyText}
              </li>
            ) : (
              filtered.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isActive = idx === activeIndex;
                return (
                  <li
                    key={opt.value}
                    id={`${listboxId}-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => select(opt.value)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={[
                      "px-3 py-2 text-body cursor-pointer transition-colors",
                      isSelected ? "text-accent font-medium" : "text-text",
                      isActive || isSelected ? "bg-accent-muted" : "hover:bg-accent-muted",
                    ].join(" ")}
                  >
                    {opt.label}
                  </li>
                );
              })
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
